import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Chip, IconButton, Modal, Typography } from "@mui/material";
import React from "react";

import { TaskInterface } from "../../common/interfaces";
import { TaskScopeLabel, TypeTaskScope } from "../enum/taskScopeEnum";
import { TaskStatusColor, TaskStatusLabel } from "../enum/taskStatusEnum";

interface TaskViewModalProps {
  open: boolean;
  onClose: () => void;
  task?: TaskInterface | null;
}

/**
 * Só leitura: o card corta título e descrição em uma linha para caber no
 * quadro, então é aqui que se lê o texto inteiro.
 */
const TaskViewModal: React.FC<TaskViewModalProps> = ({
  open,
  onClose,
  task
}) => {
  if (!task) return null;

  const formatDate = (value: string | null) =>
    value
      ? new Date(value).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })
      : "—";

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="modal-task-view">
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          border: "1px solid var(--border-strong)",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
          width: { xs: "90vw", sm: "520px" },
          maxHeight: "90vh",
          overflowY: "auto"
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          mb={2}
        >
          <Typography
            variant="h6"
            sx={{ wordBreak: "break-word", lineHeight: 1.3 }}
          >
            {task.task_title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <Chip
            label={TaskStatusLabel[task.task_status]}
            size="small"
            sx={{
              fontWeight: 700,
              color: "#fff",
              bgcolor: TaskStatusColor[task.task_status]
            }}
          />
          <Chip
            label={TaskScopeLabel[task.task_scope]}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Typography
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: task.task_description ? "text.primary" : "text.disabled",
            fontStyle: task.task_description ? "normal" : "italic",
            mb: 3
          }}
        >
          {task.task_description || "Sem descrição."}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: 0.75,
            fontSize: 13,
            color: "text.secondary"
          }}
        >
          <span>
            <strong>Data limite:</strong> {formatDate(task.task_due_date)}
          </span>
          {task.completed_at && (
            <span>
              <strong>Concluída em:</strong> {formatDate(task.completed_at)}
            </span>
          )}
          {task.user?.user_name && (
            <span>
              <strong>Criada por:</strong> {task.user.user_name}
            </span>
          )}
          {task.task_scope === TypeTaskScope.GROUP && (
            <span>
              <strong>Responsáveis:</strong>{" "}
              {task.assignees?.length
                ? task.assignees.map((a) => a.TB_USER.user_name).join(", ")
                : "ninguém ainda — livre para quem pegar"}
            </span>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button onClick={onClose} variant="contained" color="primary">
            Fechar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TaskViewModal;
